import { useMemo } from "react";
import cn from "classnames";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { AirCarriersSelect, useAirCarriersQuery } from "entities/AirCarriers";
import { ParcelCarriersSelect, useParcelCarriersQuery } from "entities/ParcelCarriers";
import { SealineSelect, useSealinesQuery } from "entities/Sealines";
import { searchFilterStore } from "entities/SearchFilter";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { CustomSelect } from "shared/ui/CustomSelect";
import { ShipmentTypeAirIcon, ShipmentTypeSeaIcon } from "shared/ui/Icons";
import styles from "./SearchFilter.module.scss";

type ShipmentTypeOptionType = {
  value: ValueOf<typeof ShipmentType>;
  label: string;
  icon?: JSX.Element;
};

const shipmentTypeIcons: Record<ValueOf<typeof ShipmentType>, ShipmentTypeOptionType["icon"]> = {
  [ShipmentType.SEA]: <ShipmentTypeSeaIcon />,
  [ShipmentType.AIR]: <ShipmentTypeAirIcon />,
  // Disabled shipment types for now — keep only Sea and Air in the select.
  [ShipmentType.RAIL]: undefined,
  [ShipmentType.ROAD]: undefined,
  [ShipmentType.PARCEL]: undefined,
};

/** Only Sea and Air are available in the Shipment type select. */
const ENABLED_SHIPMENT_TYPES: ValueOf<typeof ShipmentType>[] = [ShipmentType.SEA, ShipmentType.AIR];

type SearchFilterProps = {
  isShipmentTypeError?: boolean;
};

export const SearchFilter: RFC<SearchFilterProps> = ({ isShipmentTypeError = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const shipment = useAppSelector((state) => searchFilterStore.selectors.shipment(state));
  const isShipmentTypeConfirmed = useAppSelector((state) => searchFilterStore.selectors.isShipmentTypeConfirmed(state));
  const isGlobalTracking = useAppSelector((state) => searchFilterStore.selectors.isGlobalTracking(state));
  const { sealines } = useSealinesQuery({ requestOnMount: ShipmentTypeChecker.isSea(shipment) });
  const { airCarriers } = useAirCarriersQuery({ requestOnMount: ShipmentTypeChecker.isAir(shipment) });
  const { parcelCompanies } = useParcelCarriersQuery({ requestOnMount: ShipmentTypeChecker.isParcel(shipment) });
  const carrier = useAppSelector((state) => searchFilterStore.selectors.carrier(state));
  const allowedShipmentTypes = useAppSelector((state) => searchFilterStore.selectors.allowedShipmentTypes(state));

  // No carrier select for shipment-type=air - only Sea and Parcel show one here.
  const canShowCarrierSelect = ShipmentTypeChecker.isSea(shipment) || ShipmentTypeChecker.isParcel(shipment);
  const isCarrierSelectDisabled = isGlobalTracking && !isShipmentTypeConfirmed;

  const shipmentTypeOptions: ShipmentTypeOptionType[] = allowedShipmentTypes
    .filter((type) => ENABLED_SHIPMENT_TYPES.includes(type))
    .map((type) => ({
      value: type,
      label: t(type),
      icon: shipmentTypeIcons[type],
    }));

  const carrierSelect = useMemo(() => {
    const carrierMenuClassNames = isGlobalTracking
      ? {
          menu: styles.globalCarrierMenu,
        }
      : undefined;

    switch (shipment) {
      case ShipmentType.SEA:
        return (
          <SealineSelect
            className={isGlobalTracking ? styles.globalCarrierSelect : styles.standaloneCarrierSelect}
            placeholderDataTestId="all-carriers-select"
            value={carrier?.scacCodes?.[0]}
            showAllCarriersOption
            isDropDownMenuRelative={false}
            isDisabled={isCarrierSelectDisabled}
            componentsClassNames={carrierMenuClassNames}
            onChange={(value) => {
              const sealine = sealines.find(
                (s) => s.code === value.value || s.identifiers?.scac_codes?.includes(value.value)
              );

              if (!sealine) {
                dispatch(searchFilterStore.actions.setCarrier(null));
                return;
              }

              const scacCodes = sealine.identifiers?.scac_codes?.length
                ? [sealine.code, ...sealine.identifiers.scac_codes.filter((code) => code !== sealine.code)]
                : [sealine.code];

              dispatch(
                searchFilterStore.actions.setCarrier({
                  carrierName: sealine.short_name || sealine.name,
                  scacCodes,
                })
              );
            }}
          />
        );
      case ShipmentType.AIR:
        return (
          <AirCarriersSelect
            className={isGlobalTracking ? styles.globalCarrierSelect : styles.standaloneCarrierSelect}
            placeholderDataTestId="all-carriers-select"
            value={carrier?.scacCodes?.[0]}
            showAllCarriersOption
            isDropDownMenuRelative={false}
            isDisabled={isCarrierSelectDisabled}
            componentsClassNames={carrierMenuClassNames}
            onChange={(value) => {
              const airline = airCarriers.find((c) => c.identifiers?.iata_code === value.value);

              if (!airline) {
                dispatch(searchFilterStore.actions.setCarrier(null));
                return;
              }

              dispatch(
                searchFilterStore.actions.setCarrier({
                  carrierName: airline.name,
                  scacCodes: [airline.identifiers.iata_code, airline.identifiers.awb_prefix].filter(Boolean),
                })
              );
            }}
          />
        );
      case ShipmentType.PARCEL:
        return (
          <ParcelCarriersSelect
            className={isGlobalTracking ? styles.globalCarrierSelect : styles.standaloneCarrierSelect}
            placeholderDataTestId="all-carriers-select"
            value={carrier?.scacCodes?.[0]}
            showAllCarriersOption
            isDropDownMenuRelative={false}
            isDisabled={isCarrierSelectDisabled}
            componentsClassNames={carrierMenuClassNames}
            onChange={(value) => {
              const parcelCompany = parcelCompanies.find((p) => p.carrierCode === value.value);

              if (!parcelCompany) {
                dispatch(searchFilterStore.actions.setCarrier(null));
                return;
              }
              dispatch(
                searchFilterStore.actions.setCarrier({
                  carrierName: parcelCompany.name,
                  scacCodes: [parcelCompany.carrierCode],
                })
              );
            }}
          />
        );
      default:
        return null;
    }
  }, [
    shipment,
    parcelCompanies.length,
    sealines.length,
    airCarriers.length,
    carrier?.scacCodes?.[0],
    isGlobalTracking,
    isCarrierSelectDisabled,
  ]);

  if (!isGlobalTracking) {
    return canShowCarrierSelect ? carrierSelect : null;
  }

  return (
    <>
      <CustomSelect<ShipmentTypeOptionType, false>
        className={cn(styles.shipmentTypeSelect, isShipmentTypeError && styles.error)}
        options={shipmentTypeOptions}
        value={isShipmentTypeConfirmed ? shipmentTypeOptions.find((option) => option.value === shipment) : undefined}
        placeholder={t("Type")}
        handleChange={(value) => {
          if (isShipmentTypeConfirmed && shipment === value.value) return;
          dispatch(searchFilterStore.actions.setShippingType(value.value));
          dispatch(searchFilterStore.actions.setCarrier(null));
        }}
        menuPlacement="auto"
        componentsClassNames={{
          menuList: styles.shipmentSelectMenuList,
        }}
      />
      {canShowCarrierSelect && carrierSelect}
    </>
  );
};
